/**
 * Motion system + small building blocks for clip-v2. All motion is spring or
 * eased-interpolate based and deterministic (frame → style).
 *
 * Brand rules (theme.js): flat colors, no gradients / neon / glow.
 * Emphasis is a solid color slab behind the words.
 */
import React from "react";
import { Easing, Img, interpolate, spring } from "remotion";
import { formatMoney, formatPrice } from "../scenarioExplainerTimeline";

export const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" };

export const MOTION = {
  /** Words / numbers slamming in: fast with a little overshoot. */
  slam: { damping: 13, stiffness: 190, mass: 0.7 },
  /** Cards and panels. */
  settle: { damping: 18, stiffness: 120, mass: 0.8 },
  /** Camera punch-ins. */
  punch: { damping: 16, stiffness: 140, mass: 0.9 },
  /** Bouncy pill / button. */
  bounce: { damping: 9, stiffness: 160, mass: 0.6 },
};

export function sp(frame, fps, config = MOTION.settle, delay = 0) {
  return spring({ frame: frame - delay, fps, config });
}

/** Eased 0→1 progress over [start, start + duration] frames. */
export function ease(frame, start, duration, easing = Easing.out(Easing.cubic)) {
  return interpolate(frame, [start, start + Math.max(1, duration)], [0, 1], { ...clamp, easing });
}

export function lerp(a, b, t) {
  return a + (b - a) * t;
}

/** Style for an element that springs in: scale + rise + fade. */
export function popIn(frame, fps, delay = 0, { from = 0.6, rise = 0, config = MOTION.slam } = {}) {
  const t = sp(frame, fps, config, delay);
  const o = interpolate(frame - delay, [0, 3], [0, 1], clamp);
  return {
    opacity: o,
    transform: `translateY(${(1 - t) * rise}px) scale(${lerp(from, 1, t)})`,
  };
}

function decimalsOf(value) {
  if (Number.isInteger(value)) return 0;
  return Math.abs(value) >= 1000 ? 0 : 2;
}

/** Format a number for a count-up: fixed decimals so digits don't flicker. */
export function formatCount(value, format, finalValue) {
  const decimals = decimalsOf(finalValue);
  const v = decimals === 0 ? Math.round(value) : value;
  switch (format) {
    case "signedCurrency":
      return formatMoney(v, { signed: true, decimals });
    case "price":
      return formatPrice(v);
    case "percent":
    case "signedPercent": {
      const sign = v < 0 ? "−" : format === "signedPercent" && v > 0 ? "+" : "";
      return `${sign}${Math.abs(v).toFixed(Number.isInteger(finalValue) ? 0 : 1)}%`;
    }
    case "number":
    case "signedNumber": {
      const sign = v < 0 ? "−" : format === "signedNumber" && v > 0 ? "+" : "";
      return `${sign}${Math.abs(v).toFixed(Number.isInteger(finalValue) ? 0 : 1)}`;
    }
    case "currency":
    default:
      return formatMoney(v, { decimals });
  }
}

/** Animated number: counts from `from` to `to` over [start, start+duration]. */
export function countValue(frame, { from = 0, to, start = 0, duration = 18 }) {
  const t = ease(frame, start, duration, Easing.out(Easing.exp));
  return t > 0.995 ? to : lerp(from, to, t);
}

export function toneColor(value, colors) {
  return value < 0 ? colors.negative : colors.accent;
}

/** Solid emphasis slab (hook / captions / kickers). */
export function Slab({ children, tone = "gain", colors, fontSize, style = {}, progress = 1 }) {
  const bg = tone === "loss" ? colors.negative : colors.primary;
  const fg = tone === "loss" ? colors.background : "#FFFFFF";
  return (
    <span style={{ position: "relative", display: "inline-block", padding: "0 0.14em", ...style }}>
      <span
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          top: "0.06em",
          bottom: "0.02em",
          background: bg,
          borderRadius: "0.12em",
          transform: `scaleX(${progress})`,
          transformOrigin: "left center",
        }}
      />
      <span style={{ position: "relative", color: fg }}>{children}</span>
    </span>
  );
}

export function Kicker({ text, colors, fontSize, frame, fps, delay = 0, tone = "gain" }) {
  const t = sp(frame, fps, MOTION.slam, delay);
  return (
    <div
      style={{
        display: "inline-block",
        fontSize,
        fontWeight: 900,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        opacity: interpolate(frame - delay, [0, 3], [0, 1], clamp),
        transform: `rotate(-2deg) scale(${lerp(1.4, 1, t)})`,
        transformOrigin: "left center",
      }}
    >
      <Slab tone={tone} colors={colors} fontSize={fontSize} progress={t}>
        {text}
      </Slab>
    </div>
  );
}

export function BrandMark({ brand, size, fontSize }) {
  const { colors } = brand;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: size * 0.28 }}>
      {brand.logoUrl ? (
        <Img src={brand.logoUrl} style={{ height: size, width: "auto", objectFit: "contain" }} />
      ) : (
        <div
          style={{
            width: size,
            height: size,
            borderRadius: size * 0.26,
            background: colors.primary,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#FFFFFF",
            fontWeight: 900,
            fontSize: size * 0.58,
            lineHeight: 1,
          }}
        >
          {brand.name.charAt(0).toUpperCase()}
        </div>
      )}
      <span style={{ fontSize, fontWeight: 800, color: colors.text, letterSpacing: "-0.02em" }}>{brand.name}</span>
    </div>
  );
}

export function Chip({ children, colors, fontSize, style = {} }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: `${fontSize * 0.3}px ${fontSize * 0.75}px`,
        borderRadius: fontSize * 2,
        background: colors.surface,
        color: colors.accent,
        fontSize,
        fontWeight: 800,
        letterSpacing: "0.02em",
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {children}
    </span>
  );
}

/** Labeled big readout (e.g. "MODELED P&L  −$176"). */
export function Readout({ label, value, color, colors, labelFont, valueFont, align = "left", style = {} }) {
  return (
    <div style={{ textAlign: align, ...style }}>
      <div
        style={{
          fontSize: labelFont,
          fontWeight: 800,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: colors.muted,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: valueFont,
          fontWeight: 900,
          lineHeight: 1,
          letterSpacing: "-0.03em",
          color,
          fontVariantNumeric: "tabular-nums",
          marginTop: labelFont * 0.25,
        }}
      >
        {value}
      </div>
    </div>
  );
}
